import React from 'react';

import { CheckCircleOutlined } from '@ant-design/icons';
import { Input, Select, Switch, Typography } from 'antd';
import { ErrorMessage } from 'formik';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import {
  FormInputGroupWrapper,
  FormInputWrapper,
  FormLabel,
  MessageBox,
  StationPaymentFormGrid,
  StationPaymentFormWrapper,
} from './styled';
import IdleExemptSection from './IdleExemptSection';
import { hasActivePriceProfile } from './priceProfile';

const PriceProfileNotice = styled.div`
  align-items: center;
  background-color: #caeac7;
  border: 1px solid #73cb6d;
  border-radius: 8px;
  color: #006300;
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  padding: 8px 12px;
`;

const { Title } = Typography;

const InputField = styled.input``;

const SwitchWrapper = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
`;

const IdleMessageBox = styled.div`
  padding: 8px 12px;
  background-color: #f0f5ff;
  border-left: 4px solid #1890ff;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #e6f4ff;
    border-left-color: #0050b3;
  }

  a {
    color: #0050b3;
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;


const StationPaymentForm = ({
  values,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  // Normaliza valor de idle (remove caracteres inválidos)
  const normalizeIdleValue = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  // Popula valores de idle por connector quando muda para modo CONNECTOR
  const seedIdleChargingByConnector = () => {
    if (!values.connectors?.length) return;

    values.connectors.forEach((connector, index) => {
      // Se o connector não tem valor de idle, não faz nada
      // Se tem, mantém o valor já setado
      if (connector.idleConnectorFee === undefined) {
        setFieldValue(`connectors[${index}].idleConnectorFee`, false);
      }
      if (connector.idleFeeDefaultAmount === undefined) {
        setFieldValue(`connectors[${index}].idleFeeDefaultAmount`, '');
      }
    });
  };

  const applyIdleToAllConnectors = (idleEnabled, idleAmount) => {
    values.connectors.forEach((connector, index) => {
      setFieldValue(
        `connectors[${index}].idleConnectorFee`,
        idleEnabled,
      );

      setFieldValue(
        `connectors[${index}].idleFeeDefaultAmount`,
        idleAmount,
      );
    });
  };

  const connectors = values.connectors || [];

  const allIdleConnectorFeeEqual = connectors.every(
    (connector) =>
      connector.idleConnectorFee ===
      connectors[0]?.idleConnectorFee,
  );

  const allIdleFeeAmountEqual = connectors.every(
    (connector) =>
      String(connector.idleFeeDefaultAmount || '') ===
      String(connectors[0]?.idleFeeDefaultAmount || ''),
  );

  const hasDifferentIdleValues =
    !allIdleConnectorFeeEqual || !allIdleFeeAmountEqual;

  const stationIdleConnectorFee = allIdleConnectorFeeEqual
    ? connectors[0]?.idleConnectorFee || false
    : false;

  const stationIdleFeeDefaultAmount = allIdleFeeAmountEqual
    ? connectors[0]?.idleFeeDefaultAmount || ''
    : '';

  const hasPriceProfile = hasActivePriceProfile(values.connectors);

  const paymentFieldsDisabled = !values.hasPayment || hasPriceProfile;

  return (
    <>
    <StationPaymentFormWrapper>
      {hasPriceProfile && (
        <PriceProfileNotice role='alert'>
          <CheckCircleOutlined />
          <span>
            {T(
              'Price Profile Activated (Editing is not allowed while the station has an activated Price Profile)',
            )}
          </span>
        </PriceProfileNotice>
      )}

      <StationPaymentFormGrid>
        <div style={{ width: '50%' }}>
          <FormInputWrapper>
            <FormLabel>{T('Has Payment')}:</FormLabel>
            <SwitchWrapper>
              <Switch
                name='hasPayment'
                onChange={(checked) => setFieldValue('hasPayment', checked)}
                onBlur={handleBlur}
                checked={values.hasPayment}
              />
            </SwitchWrapper>
            <ErrorMessage name='hasPayment' component={MessageBox} />
          </FormInputWrapper>

          <FormInputWrapper>
            <FormLabel>{T('Payment Charge Type Cost')}:</FormLabel>
            <Select
              name='paymentChargeTypeCost'
              onChange={(value) => {
                setFieldValue('paymentChargeTypeCost', value);
              }}
              onBlur={handleBlur}
              value={values.paymentChargeTypeCost}
              required
              disabled={hasPriceProfile}
              size='large'
              style={{ width: '100%' }}
            >
              <option value='KWH'>{T('Payment Charge Type KWH')}</option>
              <option value='DURATION'>
                {T('Payment Charge Type Duration')}
              </option>
            </Select>
            <ErrorMessage name='paymentChargeTypeCost' component={MessageBox} />
          </FormInputWrapper>
          {values.paymentChargeTypeCost === 'DURATION' ? (
            <FormInputWrapper>
              <FormLabel>{T('Cost per duration')}:</FormLabel>
              <Input
                type='number'
                name='moneyPerDurationCost'
                label='moneyPerDurationCost'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.moneyPerDurationCost}
                maxLength='5'
                allowClear
                size='large'
                disabled={hasPriceProfile}
              />
              <ErrorMessage
                name='moneyPerDurationCost'
                component={MessageBox}
              />
            </FormInputWrapper>
          ) : (
            <FormInputWrapper>
              <FormLabel>{T('Cost per kWh')}:</FormLabel>
              <Input
                type='number'
                name='moneyPerKilowattCost'
                label='moneyPerKilowattCost'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.moneyPerKilowattCost}
                maxLength='5'
                allowClear
                size='large'
                disabled={hasPriceProfile}
              />
              <ErrorMessage
                name='moneyPerKilowattCost'
                component={MessageBox}
              />
            </FormInputWrapper>
          )}
        </div>

        <div style={{ marginLeft: 24, width: '50%' }}>
          <FormInputWrapper>
            <FormLabel>{T('Payment Charge By Income')}:</FormLabel>
            <Select
              name='paymentChargeByIncome'
              onChange={(value) => {
                setFieldValue('paymentChargeByIncome', value);
              }}
              onBlur={handleBlur}
              value={values.paymentChargeByIncome}
              required
              disabled={paymentFieldsDisabled}
              size='large'
              style={{ width: '100%' }}
            >
              <option value='CHARGE_BOX'>
                {T('Payment Charge By Income Station')}
              </option>
              <option value='CONNECTOR'>
                {T('Payment Charge By Income Connector')}
              </option>
            </Select>
            <ErrorMessage name='paymentChargeByIncome' component={MessageBox} />
          </FormInputWrapper>
          <FormInputWrapper>
            <FormLabel>{T('Payment Charge Type Income')}:</FormLabel>
            <Select
              name='paymentChargeTypeIncome'
              onChange={(value) => {
                setFieldValue('paymentChargeTypeIncome', value);
              }}
              onBlur={handleBlur}
              value={values.paymentChargeTypeIncome}
              required
              disabled={paymentFieldsDisabled}
              size='large'
              style={{ width: '100%' }}
            >
              <option value='KWH'>{T('Payment Charge Type KWH')}</option>
              <option value='DURATION'>
                {T('Payment Charge Type Duration')}
              </option>
            </Select>
            <ErrorMessage
              name='paymentChargeTypeIncome'
              component={MessageBox}
            />
          </FormInputWrapper>
          <FormInputWrapper>
            <FormLabel>{T('Income per Transaction')}:</FormLabel>
            <Input
              type='number'
              name='moneyPerTransactionIncome'
              label='moneyPerTransactionIncome'
              onChange={handleChange}
              disabled={paymentFieldsDisabled}
              onBlur={handleBlur}
              value={values.moneyPerTransactionIncome}
              maxLength='5'
              allowClear
              size='large'
            />
            <ErrorMessage
              name='moneyPerTransactionIncome'
              component={MessageBox}
            />
          </FormInputWrapper>
          <FormInputWrapper>
            <FormLabel>{T('Tax Income')}:</FormLabel>
            <Input
              type='number'
              name='taxIncome'
              label='taxIncome'
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.taxIncome}
              maxLength='5'
              allowClear
              size='large'
              disabled={paymentFieldsDisabled}
            />
            <ErrorMessage name='taxIncome' component={MessageBox} />
          </FormInputWrapper>
          {values.paymentChargeByIncome === 'CHARGE_BOX' ? (
            <FormInputGroupWrapper>
              {values.paymentChargeTypeIncome === 'DURATION' ? (
                <FormInputWrapper>
                  <FormLabel>{T('Income per Duration')}:</FormLabel>
                  <Input
                    type='number'
                    name='moneyPerDurationIncome'
                    label='moneyPerDurationIncome'
                    onChange={handleChange}
                    disabled={paymentFieldsDisabled}
                    onBlur={handleBlur}
                    value={values.moneyPerDurationIncome}
                    maxLength='5'
                    allowClear
                    size='large'
                  />
                  <ErrorMessage
                    name='moneyPerDurationIncome'
                    component={MessageBox}
                  />
                </FormInputWrapper>
              ) : (
                <FormInputWrapper>
                  <FormLabel>{T('Income per kWh')}:</FormLabel>
                  <Input
                    type='number'
                    name='moneyPerKilowattIncome'
                    label='moneyPerKilowattIncome'
                    onChange={handleChange}
                    disabled={paymentFieldsDisabled}
                    onBlur={handleBlur}
                    value={values.moneyPerKilowattIncome}
                    maxLength='5'
                    allowClear
                    size='large'
                  />
                  <ErrorMessage
                    name='moneyPerKilowattIncome'
                    component={MessageBox}
                  />
                </FormInputWrapper>
              )}
            </FormInputGroupWrapper>
          ) : values.connectors?.length ? (
            values.connectors
              ?.sort((a, b) => a.connectorId - b.connectorId)
              .map((connector, index) => (
                <FormInputGroupWrapper>
                  {values.paymentChargeTypeIncome === 'DURATION' ? (
                    <FormInputWrapper>
                      <FormLabel>
                        {`${T('Connector ID')} ${values.connectors[index].connectorId
                          } - ${T('Income per Duration')}`}
                        :
                      </FormLabel>
                      <Input
                        type='number'
                        name={`connectors[${index}].moneyPerDurationIncome`}
                        label={`connectors[${index}].moneyPerDurationIncome`}
                        onChange={handleChange}
                        disabled={paymentFieldsDisabled}
                        onBlur={handleBlur}
                        value={values.connectors[index].moneyPerDurationIncome}
                        maxLength='5'
                        allowClear
                        size='large'
                      />
                      <ErrorMessage
                        name={`connectors[${index}].moneyPerDurationIncome`}
                        component={MessageBox}
                      />
                    </FormInputWrapper>
                  ) : (
                    <FormInputWrapper>
                      <FormLabel>
                        {`${T('Connector ID')} ${values.connectors[index].connectorId
                          } - ${T('Income per kWh')}`}
                        :
                      </FormLabel>
                      <Input
                        type='number'
                        name={`connectors[${index}].moneyPerKilowattIncome`}
                        label={`connectors[${index}].moneyPerKilowattIncome`}
                        onChange={handleChange}
                        disabled={paymentFieldsDisabled}
                        onBlur={handleBlur}
                        value={values.connectors[index].moneyPerKilowattIncome}
                        maxLength='5'
                        allowClear
                        size='large'
                      />
                      <ErrorMessage
                        name={`connectors[${index}].moneyPerKilowattIncome`}
                        component={MessageBox}
                      />
                    </FormInputWrapper>
                  )}
                </FormInputGroupWrapper>
              ))
          ) : null}

          {values.paymentChargeByIncome !== 'CONNECTOR' ? (
            <>
              {/* CONFIG GLOBAL */}
              <FormInputGroupWrapper
                style={{
                  flexDirection: 'column',
                  gap: '0px',
                  marginBottom: '20px',
                }}
              >
                <FormInputWrapper style={{ marginBottom: '8px' }}>
                  <FormLabel>
                    {T('Enable Idle Charging')}:
                  </FormLabel>

                  <SwitchWrapper>
                    <Switch
                      checked={connectors.some(c => c.idleConnectorFee)}
                      onChange={(checked) => {
                        values.connectors?.forEach((connector, index) => {
                          setFieldValue(
                            `connectors[${index}].idleConnectorFee`,
                            checked,
                          );
                        });
                      }}
                      disabled={paymentFieldsDisabled}
                    />
                  </SwitchWrapper>
                </FormInputWrapper>

                <FormInputWrapper>
                  <FormLabel>
                    {T('Idle Charging Cost')}:
                  </FormLabel>

                  <Input
                    type='number'
                    value={
                      hasDifferentIdleValues
                        ? ''
                        : stationIdleFeeDefaultAmount
                    }
                    placeholder={
                      hasDifferentIdleValues
                        ? T('Connectors have different idle values')
                        : '0'
                    }
                    size='large'
                    disabled={
                      paymentFieldsDisabled ||
                      !stationIdleConnectorFee
                    }
                    onChange={(e) => {
                      const value = e.target.value;

                      values.connectors?.forEach((connector, index) => {
                        setFieldValue(
                          `connectors[${index}].idleFeeDefaultAmount`,
                          value,
                        );
                      });
                    }}
                  />
                </FormInputWrapper>

                {!hasPriceProfile && (
                  <IdleMessageBox
                    style={{ marginTop: '-20px' }}
                    onClick={() => {
                      setFieldValue('paymentChargeByIncome', 'CONNECTOR');
                      seedIdleChargingByConnector();
                    }}
                  >
                    <div >
                      {T('Need different values per connector?')}
                    </div>

                    <div style={{ marginTop: '2px' }}>
                      <a>{T('Configure individually')}</a>
                    </div>
                  </IdleMessageBox>
                )}
              </FormInputGroupWrapper>
            </>
          ) : null}
          {values.paymentChargeByIncome === 'CONNECTOR' &&
            values.connectors?.length ? (
            <>
              {[...values.connectors]
                ?.sort((a, b) => a.connectorId - b.connectorId)
                .map((connector) => {
                  const originalIndex = values.connectors.findIndex(c => c.connectorId === connector.connectorId);

                  return (
                    // Forçamos o GroupWrapper a ser COLUMN para o switch ficar em cima do input
                    <FormInputGroupWrapper
                      key={`idle-group-${connector.connectorId}`}
                      style={{ flexDirection: 'column', gap: '0px', marginBottom: '5px' }}
                    >

                      {/* Bloco do Switch */}
                      <FormInputWrapper style={{ marginBottom: '8px' }}>
                        <FormLabel>
                          {`${T('Connector ID')} ${connector.connectorId} - ${T('Enable Idle Charging')}`}:
                        </FormLabel>
                        <SwitchWrapper>
                          <Switch
                            name={`connectors[${originalIndex}].idleConnectorFee`}
                            onChange={(checked) => {
                              setFieldValue(
                                `connectors[${originalIndex}].idleConnectorFee`,
                                checked,
                              );
                            }}
                            onBlur={handleBlur}
                            checked={values.connectors[originalIndex].idleConnectorFee || false}
                            disabled={paymentFieldsDisabled}
                          />
                        </SwitchWrapper>
                      </FormInputWrapper>

                      {/* Bloco do Valor - Agora ele vai descer naturalmente porque o pai é column */}
                      <FormInputWrapper>
                        <FormLabel>{T('Idle Charging Cost')}:</FormLabel>
                        <Input
                          type='number'
                          name={`connectors[${originalIndex}].idleFeeDefaultAmount`}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={connector.idleFeeDefaultAmount}
                          placeholder="0"
                          maxLength='5'
                          allowClear
                          size='large'
                          style={{ width: '100%' }}
                          disabled={paymentFieldsDisabled || !connector.idleConnectorFee}
                        />
                        <ErrorMessage
                          name={`connectors[${originalIndex}].idleFeeDefaultAmount`}
                          component={MessageBox}
                        />
                      </FormInputWrapper>

                      {/* SP-MV2-1198: per-connector idle exemption override */}
                      <div style={{ marginTop: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Switch
                            checked={values.connectors[originalIndex].idleExemptAlwaysCharge != null}
                            disabled={hasPriceProfile}
                            onChange={(checked) => {
                              if (checked) {
                                // Seed override with the station's current values
                                setFieldValue(`connectors[${originalIndex}].idleExemptAlwaysCharge`, values.idleExemptAlwaysCharge ?? true);
                                setFieldValue(`connectors[${originalIndex}].idleExemptCustomizePerDay`, values.idleExemptCustomizePerDay ?? false);
                                setFieldValue(`connectors[${originalIndex}].idleExemptSchedules`, [...(values.idleExemptSchedules ?? [])]);
                              } else {
                                // Explicit reset — send `[]` for schedules so backend wipes any
                                // previously-persisted connector slots (payload without the field
                                // would leave stale data intact).
                                setFieldValue(`connectors[${originalIndex}].idleExemptAlwaysCharge`, null);
                                setFieldValue(`connectors[${originalIndex}].idleExemptCustomizePerDay`, null);
                                setFieldValue(`connectors[${originalIndex}].idleExemptSchedules`, []);
                              }
                            }}
                          />
                          <span>{T('Use custom idle exemption per connector')}</span>
                        </label>
                        {/* Full IdleExemptSection lives in a full-width block below the grid */}
                      </div>
                    </FormInputGroupWrapper>
                  );
                })}
            </>
          ) : null}
        </div>
      </StationPaymentFormGrid>
    </StationPaymentFormWrapper>

    {/* SP-MV2-1198: idle exemption window rendered OUTSIDE the 870px form wrapper
        so it can use the full width of the payment tab (much larger horizontal space). */}
    {values.paymentChargeByIncome !== 'CONNECTOR' && (
      <div style={{ width: '100%', marginTop: 24 }}>
        <IdleExemptSection
          values={values}
          setFieldValue={setFieldValue}
          namespace=''
          disabled={hasPriceProfile}
        />
      </div>
    )}

    {/* SP-MV2-1198: per-connector idle exemption overrides.
        Rendered full-width below the payment grid; one card per connector whose
        "Use custom idle exemption per connector" toggle is ON. */}
    {values.paymentChargeByIncome === 'CONNECTOR' &&
      values.connectors?.length > 0 &&
      values.connectors.some((c) => c.idleExemptAlwaysCharge != null) && (
      <div style={{ width: '100%', marginTop: 32 }}>
        <Title level={5} style={{ marginBottom: 16 }}>
          {T('Per-connector idle exemption overrides')}
        </Title>
        {[...values.connectors]
          .sort((a, b) => a.connectorId - b.connectorId)
          .filter((c) => c.idleExemptAlwaysCharge != null)
          .map((connector) => {
            const idx = values.connectors.findIndex((c) => c.connectorId === connector.connectorId);
            return (
              <div
                key={`idle-exempt-override-${connector.connectorId}`}
                style={{
                  marginBottom: 24,
                  padding: '16px 20px',
                  border: '1px solid #e8e8e8',
                  borderRadius: 8,
                }}
              >
                <Title level={5} style={{ marginBottom: 12 }}>
                  {T('Connector ID')} {connector.connectorId}
                </Title>
                <IdleExemptSection
                  values={values}
                  setFieldValue={setFieldValue}
                  namespace={`connectors[${idx}]`}
                  showTitle={false}
                  disabled={hasPriceProfile}
                />
              </div>
            );
          })}
      </div>
    )}
    </>
  );
};

export default StationPaymentForm;
